import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography,
  Paper,
  Button,
  Box,
  Stack,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import CustomerLayout from "../CustomerLayout";
import { API } from "../../../config";

function getToken() {
  return (
    localStorage.getItem("customer_token") ||
    localStorage.getItem("token")
  );
}

const OrderBidsAndNegotiations = () => {
  const { orderId } = useParams();
  const history = useHistory();

  const [bids, setBids] = useState([]);
  const [threads, setThreads] = useState([]);
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const cleanOrderId = String(orderId || '').replace(/^ORD-/i, '').trim();

  const loadBids = async () => {
    try {
      setLoading(true);
      setErr("");

      const token = getToken();

      if (!token) {
        throw new Error("Your session expired. Please sign in again.");
      }

      // Primary source for QA 9-4:
      // this loads the real negotiation threads/messages/offers created by the driver.
      const threadsRes = await fetch(
        `${API}/api/negotiations/order/${cleanOrderId}/threads`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!threadsRes.ok) {
        throw new Error(`Failed to load negotiation threads (${threadsRes.status})`);
      }

      const threadsPayload = await threadsRes.json();

      const threadList = Array.isArray(threadsPayload)
        ? threadsPayload
        : threadsPayload.threads || threadsPayload.rows || [];

      setThreads(threadList);
      setOrderInfo(threadsPayload.order || null);

      // Legacy Bid-table fallback.
      // Do not let old Bid-table errors break the negotiation screen.
      try {
        const bidsRes = await fetch(`${API}/api/orders/${cleanOrderId}/bids`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (bidsRes.ok) {
          const data = await bidsRes.json();

          const list = Array.isArray(data)
            ? data
            : data.bids || data.rows || data.results || [];

          setBids(list);
        } else {
          setBids([]);
        }
      } catch (_) {
        setBids([]);
      }
    } catch (e) {
      setErr(e.message || "Error loading negotiations");
      setThreads([]);
      setBids([]);
      setOrderInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadBids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const acceptBid = async (bidId) => {
    try {
      setErr("");

      const res = await fetch(
        `${API}/api/orders/${cleanOrderId}/bids/${bidId}/accept`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Accept bid failed (${res.status})`);
      }

      alert("Bid accepted. Load is now assigned.");
      history.push("/customer/order-history");
    } catch (e) {
      setErr(e.message || "Error accepting bid");
    }
  };

  const negotiateWithCarrier = async (carrierId) => {
    try {
      setErr("");

      const res = await fetch(
        `${API}/api/negotiations/order/${cleanOrderId}/thread`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            courier_id: carrierId,
            carrier_id: carrierId,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Open negotiation failed (${res.status})`);
      }

      const thread = await res.json();

      const threadId =
        thread.id ||
        thread.thread_id ||
        thread.threadId ||
        thread.data?.id ||
        thread.data?.thread_id;

      if (!threadId) {
        console.error("Thread response:", thread);
        throw new Error("Negotiation thread was not returned.");
      }

      history.push(`/customer/negotiations/thread/${threadId}`);
    } catch (e) {
      setErr(e.message || "Error opening negotiation");
    }
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 4 },
            borderRadius: 3,
            maxWidth: 900,
            mx: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: {
              xs: "none",
              md: "0 10px 24px rgba(15, 23, 42, 0.06)",
            },
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}
            >
             Negotiation
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Review bids, exchange messages, and negotiate rates for this load.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Load {orderInfo?.orderNumber || `ORD-${cleanOrderId}`}
            </Typography>
          </Box>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

                    {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : threads.length === 0 && bids.length === 0 ? (
            <Alert severity="info">
              No carrier bids or negotiation messages yet. When a driver or carrier
              sends a message, offer, or bid for this load, it will appear here.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {threads.length > 0 && (
  <Stack spacing={2} sx={{ mb: 2 }}>
    {threads.map((thread) => {
      const threadId = thread.id || thread.thread_id;
      const amountCents =
        thread.latest_offer_amount_cents ||
        thread.amount_cents ||
        0;

      const hasOffer = Number(amountCents) > 0;
      const latestOfferStatus = String(thread.latest_offer_status || '').toUpperCase();

      return (
        <Paper
          key={`thread-${threadId}`}
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff',
          }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography fontWeight={800}>
                  Driver / Carrier Negotiation
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {thread.order_number || `ORD-${cleanOrderId}`} · Thread #{threadId}
                </Typography>
              </Box>

              <Chip
                label={
                  latestOfferStatus ||
                  (hasOffer ? 'Offer' : 'Chat')
                }
                color={
                  latestOfferStatus === 'PENDING'
                    ? 'warning'
                    : latestOfferStatus === 'ACCEPTED'
                    ? 'success'
                    : 'primary'
                }
                variant="outlined"
              />
            </Box>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Driver / Carrier
            </Typography>

            <Typography fontWeight={700}>
              {thread.courier_name || `Carrier #${thread.courier_id}`}
            </Typography>

            {Number(thread.message_count || 0) > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Latest Message
                </Typography>

                <Typography sx={{ wordBreak: 'break-word' }}>
                  {thread.last_message_body || 'Message available'}
                </Typography>
              </>
            )}

            {hasOffer && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Latest Offer
                </Typography>

                <Typography fontWeight={800}>
                  ${(Number(amountCents) / 100).toFixed(2)}
                </Typography>

                {thread.latest_offer_notes && (
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    Notes: {thread.latest_offer_notes}
                  </Typography>
                )}
              </>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                history.push(`/customer/negotiations/thread/${threadId}`)
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Open Bids / Chat
            </Button>
          </Stack>
        </Paper>
      );
    })}
  </Stack>
)}


              {bids.map((b) => {
                const bidId = b.id || b.bid_id;
                const carrierId = b.courier_id || b.carrier_id;
                const amountCents = b.amount_cents || b.amountCents || 0;

                return (
                  <Paper
                    key={bidId}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Typography fontWeight={800}>
                            Carrier Bid
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Bid #{bidId}
                          </Typography>
                        </Box>

                        <Chip
                          label={`$${((amountCents || 0) / 100).toFixed(2)}`}
                          color="primary"
                          variant="outlined"
                        />
                      </Box>

                      <Divider />

                      <Typography variant="body2" color="text.secondary">
                        Carrier
                      </Typography>

                      <Typography fontWeight={700}>
                        {carrierId ? `Carrier #${carrierId}` : "—"}
                      </Typography>

                      {b.notes && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Notes
                          </Typography>

                          <Typography>{b.notes}</Typography>
                        </>
                      )}

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ pt: 1 }}
                      >
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => acceptBid(bidId)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                          }}
                        >
                          Accept Bid
                        </Button>

                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => negotiateWithCarrier(carrierId)}
                          disabled={!carrierId}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                          }}
                        >
                          Open Chat / Negotiate
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}

          <Button
            variant="text"
            fullWidth
            onClick={() => history.push("/customer/order-history")}
            sx={{
              mt: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Back to Load History
          </Button>
        </Paper>
      </Box>
    </CustomerLayout>
  );
};

export default OrderBidsAndNegotiations;